/**
 * The help pages: questions people ask, answered the way Affily actually
 * works. Pure data, shared by the site (/help and /help/:slug), the build-time
 * prerender and the sitemap, so a page can never be listed that does not exist.
 *
 * Figures come from the same constants the product runs on — the
 * cookie-window limits, the recurring cap — so an answer cannot drift from the
 * rule it describes. Editing an answer is a change to this file, reviewed like
 * any other.
 *
 * Affily's fee rates are never quoted here: these pages are public, and the
 * rates are shown only to signed-in accounts, on their Pricing page.
 *
 * Answers are short paragraphs and lists. Inline, `[text](/path)` is a link
 * and `**text**` is bold; nothing else is interpreted.
 */
import {
  MIN_COOKIE_WINDOW_DAYS,
  MAX_COOKIE_WINDOW_DAYS,
  MAX_RECURRING_MONTHS,
  MAX_PRIZES_PER_PROGRAM,
  BANK_TRANSFER_MIN_CENTS,
  BANK_TRANSFER_CURRENCIES,
  POST_SUBMISSION_GRACE_DAYS,
} from './constants.js';
import { formatCents } from './money.js';

const bankTransferFrom = formatCents(BANK_TRANSFER_MIN_CENTS, {
  currency: BANK_TRANSFER_CURRENCIES[0],
  dropZeroCents: true,
});

export const HELP_SECTIONS = [
  { key: 'start', title: 'Getting started' },
  { key: 'creators', title: 'For creators' },
  { key: 'brands', title: 'For brands' },
  { key: 'programs', title: 'Dates and currencies' },
  { key: 'competitions', title: 'Competitions and prizes' },
  { key: 'account', title: 'Privacy and your account' },
];

/**
 * @typedef {{ slug: string, section: string, question: string,
 *   answer: Array<string | { list: string[] }>, related?: string[], keywords?: string }} HelpArticle
 */

/** @type {HelpArticle[]} */
export const HELP_ARTICLES = [
  // ── Getting started ──────────────────────────────────────────────────────
  {
    slug: 'what-is-affily',
    section: 'start',
    question: 'What is Affily?',
    answer: [
      'A marketplace where brands publish partner programs and creators join them. A creator shares their own link (or code); Affily tracks what their audience does — a sale, a sign-up, a booking — and pays the creator what the program says, through Affily.',
      'A program’s full terms are public before anyone applies: the rate, how long a click counts, when it pays, and what it has actually paid out so far. [Browse the programs](/programs). A brand can also run a [private program](/help/private-programs), whose terms only the creators it invites or accepts see.',
    ],
    related: ['is-affily-free', 'why-are-terms-public', 'how-do-i-join-a-program'],
    keywords: 'about marketplace affiliate partner',
  },
  {
    slug: 'is-affily-free',
    section: 'start',
    question: 'Is Affily free?',
    answer: [
      'Joining is free on both sides. Affily charges a fee on each commission, split evenly between the brand and the creator: the creator’s half comes out of the commission, and the brand pays its half on top. The rate depends on what the program pays for — digital products, anything else, or a competition prize.',
      'Once you’re signed in, the Pricing page in your dashboard shows your side’s rates. Every commission line shows the gross amount, the fee and what the creator receives, so the arithmetic is never hidden.',
    ],
    related: ['how-do-i-get-paid', 'how-do-i-pay-creators'],
    keywords: 'price pricing cost fee commission percent split',
  },
  {
    slug: 'why-are-terms-public',
    section: 'start',
    question: 'Why are program terms public?',
    answer: [
      'So a creator can compare offers before spending their audience’s attention, and so a brand competes on its terms rather than on who asked.',
      'A program’s real numbers are public too — clicks, conversions, conversion rate — measured by Affily, not reported by the brand. [How transparency works](/transparency).',
      'A brand that would rather not publish its rates can make a program [private](/help/private-programs): it isn’t listed anywhere, and its terms go only to the creators the brand invites or accepts.',
    ],
    related: ['what-is-public', 'transparency-score', 'private-programs'],
    keywords: 'transparent rates public terms',
  },

  // ── Creators ─────────────────────────────────────────────────────────────
  {
    slug: 'how-do-i-join-a-program',
    section: 'creators',
    question: 'How do I join a program?',
    answer: [
      'Open the program and apply. Some programs approve applications automatically — the program page says so — and others are reviewed by the brand. Once you are in, your dashboard has your link.',
      'A program takes applications either any time until it ends, or during an application window before it starts. The program page shows which, and when the window opens and closes. A brand can also invite you directly.',
    ],
    related: ['application-windows', 'how-do-tracking-links-work', 'when-does-a-program-end'],
    keywords: 'apply application approve invite',
  },
  {
    slug: 'pitch-a-brand',
    section: 'creators',
    question: 'How do I pitch a brand a campaign?',
    answer: [
      'Open the brand’s page and press “Pitch a campaign”. Write the brief — your idea, who your audience is, and what you’d make, where and how many — then your conditions: how you’d like to be paid (a fixed fee per post, a % of each sale, a fixed amount per sale or lead, or the product only), whether the brand may reuse your content, and any exclusivity. Preview shows exactly what the brand will see.',
      'The brand can say yes, and may adjust the amount first; Affily then sets it up as a [private program](/help/private-programs) for you and sends you an invitation with the final terms, which you accept as with any invitation. Or it can say no, with a note. Either way you get an email.',
      'You can have one pitch waiting with each brand, and withdraw one to send another. Brands can switch pitches off; then the button isn’t shown.',
    ],
    related: ['how-do-i-join-a-program', 'private-programs'],
    keywords: 'pitch propose suggest campaign brief collaboration offer',
  },
  {
    slug: 'how-do-tracking-links-work',
    section: 'creators',
    question: 'How does my link get me credited?',
    answer: [
      'Your link sends the visitor to the brand’s site with a click id. When that visitor buys, books or signs up, the brand’s site (or its store) reports it with the click id, and it is credited to you.',
      `The brand sets how long after a click it still counts — the cookie window, between ${MIN_COOKIE_WINDOW_DAYS} and ${MAX_COOKIE_WINDOW_DAYS} days — and the window you joined under is the one that applies to you, even if the brand changes it later. The most recent click wins.`,
      'Repeated clicks from the same visitor within a day count as one unique click.',
    ],
    related: ['creator-codes', 'why-wasnt-my-sale-credited'],
    keywords: 'link click cookie window attribution last click',
  },
  {
    slug: 'creator-codes',
    section: 'creators',
    question: 'What is a creator code?',
    answer: [
      'Some programs give each creator a discount code, such as RAE10. A sale using your code is credited to you with no click at all, and with no cookie window — the shopper named you. A code beats a click from another creator.',
      'An action that pays once per visitor (a sign-up, say) is never paid by code, because a code cannot tell visitors apart.',
    ],
    related: ['how-do-tracking-links-work'],
    keywords: 'code coupon discount podcast',
  },
  {
    slug: 'what-do-i-see-about-my-referrals',
    section: 'creators',
    question: 'What can I see about the people I referred?',
    answer: [
      'Under Partnerships, “What your referrals did” lists each action credited to you — a booking, a sale — with when it happened, whether it came through your link or your code, and what you earned from it.',
      'You never see the customer, their order number or what they paid. Those are the brand’s. Your earnings show the order value only where your commission is a percentage of it, so you can check the sum.',
    ],
    related: ['how-do-i-get-paid', 'visitor-privacy'],
    keywords: 'activity referrals bookings anonymous customer',
  },
  {
    slug: 'how-do-i-get-paid',
    section: 'creators',
    question: 'How and when do I get paid?',
    answer: [
      'Connect a Stripe account under Payouts — that is where the money lands. Then each commission moves through four stages:',
      {
        list: [
          '**Approved** — Affily credited it to you.',
          '**Settled** — the brand confirmed the order is real.',
          '**Paid** — the brand released it and Stripe transferred it to you.',
          '**Reversed** — taken back, with the reason shown.',
        ],
      },
      `The brand publishes how soon it pays (Net 15, Net 30…) and releases each payout itself; nothing is paid automatically. Your half of Affily’s fee comes out of each commission, and every line shows gross, fee and what you receive; your Pricing page shows the rates.`,
    ],
    related: ['is-affily-free', 'which-currencies', 'how-are-prizes-paid'],
    keywords: 'payout stripe money paid earnings settle',
  },
  {
    slug: 'pay-per-post',
    section: 'creators',
    question: 'How does pay per post work?',
    answer: [
      'On a program that pays a fixed amount per post, open the partnership in your dashboard and send the link to each post you made, with a note if it helps. The brand opens it, checks it is what you agreed, and approves it — that is what pays it — or rejects it with a note.',
      'An approved post becomes a commission at the amount you joined on, even if the program’s rate changes later, and is settled and paid out like any other. Each post is paid once.',
      `You can send posts until ${POST_SUBMISSION_GRACE_DAYS} days after the program ends, so something made on the last day still counts. A post still waiting can be withdrawn.`,
    ],
    related: ['how-do-i-get-paid', 'pitch-a-brand'],
    keywords: 'post video reel per post deliverable approve fixed fee content',
  },
  {
    slug: 'why-wasnt-my-sale-credited',
    section: 'creators',
    question: 'Why wasn’t a sale credited to me?',
    answer: [
      'The usual reasons, most common first:',
      {
        list: [
          'The click was older than the program’s cookie window.',
          'The brand’s site didn’t pass the click id through to its checkout, or an ad blocker stopped its tag.',
          'Another creator’s link was clicked more recently, or the shopper used someone else’s code.',
          'It happened before the program started or after it ended — a renewal, or a sale the brand recorded from an earlier action, included — and the program doesn’t keep paying after the end.',
          'The action is tracked but doesn’t pay on this program (shown as “tracked”).',
          'The order was in a different currency from the program’s.',
        ],
      },
      'The brand sees every report Affily received and exactly why each one did or didn’t earn anyone anything — ask them to check their tracking page.',
    ],
    related: ['how-do-tracking-links-work', 'when-does-a-program-end', 'tracked-only-actions'],
    keywords: 'missing not credited unattributed lost sale',
  },

  // ── Brands ───────────────────────────────────────────────────────────────
  {
    slug: 'how-do-i-set-up-tracking',
    section: 'brands',
    question: 'How do I set up tracking?',
    answer: [
      'From the program’s “Tracking & stores” page, best first:',
      {
        list: [
          '**Connect your store** — WooCommerce, Shopify or Stripe in one click. Every paid order is reported with no code, and it can’t be blocked.',
          '**From your server** — a signed report with your secret. Copy the code for Node, Python, PHP or Ruby; your credentials are already in it.',
          '**The browser tag** — for a site with no server, such as a React site taking bookings. Paste one line, then report the action when it succeeds.',
        ],
      },
      'Then watch “Is it working?” on the same page: every report arrives there within seconds, with why it did or didn’t pay.',
    ],
    related: ['tracked-only-actions', 'recording-sales-yourself'],
    keywords: 'integration pixel tag postback webhook shopify stripe woocommerce react',
  },
  {
    slug: 'tracked-only-actions',
    section: 'brands',
    question: 'Can I track something without paying for it?',
    answer: [
      'Yes. An action can be tracked only — a viewing booked, a free sign-up — so you and the creator see what their audience did, without it paying anything. It still counts towards a competition prize if one counts it.',
    ],
    related: ['recording-sales-yourself', 'how-do-competitions-work'],
    keywords: 'tracked only lead booking funnel',
  },
  {
    slug: 'recording-sales-yourself',
    section: 'brands',
    question: 'How do I record a sale that happens offline?',
    answer: [
      'Mark the action “Only I record this, in Affily” — a house sale, a signed contract. Your site and tag can then never report it, so nobody can fake one.',
      'When it happens, find the tracked action it came from (the viewing) in your activity — search by its id — and choose “Record sale”. It goes to the creator who brought that viewing, however long ago it was, and pays what that creator’s terms say. After the program has ended it is still recorded against them, but paid only if the program keeps paying after the end. The screen shows the amount before you confirm.',
      'Recorded one by mistake? Undo it while it is still unsettled, and record it again.',
    ],
    related: ['tracked-only-actions', 'when-does-a-program-end', 'can-i-change-my-terms'],
    keywords: 'manual offline sale record bonus viewing undo',
  },
  {
    slug: 'private-programs',
    section: 'brands',
    question: 'Can I keep a program’s terms private?',
    answer: [
      'Yes. In the program editor, set “Who can see the terms” to “Only creators I invite or accept”. Then:',
      {
        list: [
          'It is left out of the directory, your brand page, search engines and the sitemap.',
          'Anyone you send its link to sees your brand, the description and what you’d like creators to make, and can apply.',
          'The terms — the rate, the cookie window, what each action pays — and the program’s numbers go only to creators you invite or accept.',
        ],
      },
      'A creator who joins sees the same full terms as on any program, and keeps them as with any program.',
    ],
    related: ['why-are-terms-public', 'pitch-a-brand', 'can-i-change-my-terms'],
    keywords: 'private hidden invite only secret rates terms unlisted',
  },
  {
    slug: 'can-i-change-my-terms',
    section: 'brands',
    question: 'Can I change my program’s terms later?',
    answer: [
      'Yes, for creators who join from then on. Each creator keeps the terms they joined under — the rate, the cookie window, what each action pays — so a change never shrinks what someone has already worked for.',
      `Recurring commissions are capped at ${MAX_RECURRING_MONTHS} billing cycles; there is no lifetime option.`,
    ],
    related: ['which-currencies', 'can-prizes-change'],
    keywords: 'edit rate change terms frozen',
  },
  {
    slug: 'how-do-i-pay-creators',
    section: 'brands',
    question: 'How do I pay creators?',
    answer: [
      'Add a card under Billing. Then, under Settle & pay:',
      {
        list: [
          '**Settle** the commissions you confirm — real orders, not refunded.',
          '**Pay** each creator: your card is charged what you owe — the commissions, your half of Affily’s fee, and the card fee — and once that clears the creator is paid through Stripe.',
        ],
      },
      `Owe ${bankTransferFrom} or more in euros? You can pay by bank transfer instead, with no card fee: Settle & pay gives you the account details, and once your transfer arrives you pay creators from that balance.`,
      'Nothing moves on its own, and only a person signed in can settle or pay — never an API key. A creator owed in two currencies is paid once per currency.',
    ],
    related: ['refunds', 'which-currencies', 'how-are-prizes-paid'],
    keywords: 'billing card settle pay payout bank transfer iban',
  },
  {
    slug: 'refunds',
    section: 'brands',
    question: 'What if an order is refunded?',
    answer: [
      'Don’t settle that commission: settling is line by line, so you confirm only the orders that stood. A sale you recorded yourself can be undone while it is unsettled.',
      'Once you have settled or paid a commission, it is money you confirmed, and it stays.',
    ],
    related: ['how-do-i-pay-creators', 'recording-sales-yourself'],
    keywords: 'refund cancel reverse chargeback',
  },

  // ── Dates and currencies ─────────────────────────────────────────────────
  {
    slug: 'when-does-a-program-end',
    section: 'programs',
    question: 'What happens when a program ends?',
    answer: [
      'Every program has an end date. By default it renews for another year each time it gets there; a brand running a campaign can turn that off, and then it simply ends.',
      'After the end: nobody new joins, links still take visitors to the brand’s site but credit nobody, and new actions are recorded but not paid.',
      'Renewals of subscriptions sold during the program, and sales the brand records from actions during it, are paid after the end only if the brand ticked “Keep paying after the program ends”. It is off unless the brand turns it on, and once creators have joined on it, it stays on.',
      'Once creators have joined, the dates only move in their favour: the end can move later, not earlier.',
    ],
    related: ['application-windows', 'recording-sales-yourself'],
    keywords: 'end date expire renew extend campaign',
  },
  {
    slug: 'application-windows',
    section: 'programs',
    question: 'What is an application window?',
    answer: [
      'Some programs pick their creators before they start: applications are open between two dates that close by the start date. Outside them, the program page says when applications open or that they have closed.',
      'The window only limits applications. The brand can still invite a creator until the program ends, and accept applications that arrived on time.',
    ],
    related: ['how-do-i-join-a-program', 'when-does-a-program-end'],
    keywords: 'apply window deadline intake',
  },
  {
    slug: 'which-currencies',
    section: 'programs',
    question: 'Which currencies can a program pay in?',
    answer: [
      'Euros, US dollars or pounds sterling. The brand chooses when it makes the program, and it is fixed once a creator has applied. Every commission, prize and payout on that program is in its currency.',
      'Amounts in different currencies are never added together: dashboards show each separately, and a creator owed in two is paid twice, once in each. An order reported in another currency is recorded but not paid, with the reason.',
    ],
    related: ['how-do-i-get-paid', 'how-do-i-pay-creators'],
    keywords: 'currency euro eur dollar usd pound gbp',
  },

  // ── Competitions ─────────────────────────────────────────────────────────
  {
    slug: 'how-do-competitions-work',
    section: 'competitions',
    question: 'How do competitions work?',
    answer: [
      `A program can run a competition: up to ${MAX_PRIZES_PER_PROGRAM} prizes, on top of what it already pays, between two dates. A prize goes to:`,
      {
        list: [
          'the creator who brought the most of an action — most viewings booked, say;',
          'the best single video by views, shares, likes or comments; or',
          'the first video to reach a number, such as 100,000 views.',
        ],
      },
      'Creators see their own standing and the leading figure — never who anyone else is, or their videos. The brand sees everything that was counted, so it can check before it pays.',
    ],
    related: ['how-are-videos-measured', 'first-to-a-number', 'how-are-prizes-paid'],
    keywords: 'competition contest prize video',
  },
  {
    slug: 'how-are-videos-measured',
    section: 'competitions',
    question: 'How are video views and shares measured?',
    answer: [
      'You connect TikTok, Instagram or YouTube in one click — read-only, Affily never posts — and enter your videos. Affily reads their numbers from the platform through your own connection, so nobody can type a number in or enter someone else’s video.',
      'Only videos posted after the competition starts can be entered, up to 10 each. Numbers are re-read every few hours. At the end they are taken as they stand and frozen; a reading more than a day late isn’t used, and the last one before the end counts instead.',
      'Instagram shares numbers for business and creator accounts only. YouTube reports shares a couple of days late, so those are read for three days after the end.',
    ],
    related: ['first-to-a-number', 'how-do-competitions-work'],
    keywords: 'tiktok instagram youtube connect views shares likes comments',
  },
  {
    slug: 'first-to-a-number',
    section: 'competitions',
    question: 'How is “first to 100,000 views” decided?',
    answer: [
      'It goes to the first video — on any platform — that Affily reads at or past the number. Videos close to it are read every 15 minutes, so “first” is decided to the quarter hour. Two videos past it in the same reading go to the one with more; still level, the brand decides.',
      'It can be awarded as soon as a video gets there, without waiting for the end. If none does by the end, nobody wins it.',
    ],
    related: ['how-are-videos-measured', 'how-are-prizes-paid'],
    keywords: 'first to threshold 100k views race',
  },
  {
    slug: 'how-are-prizes-paid',
    section: 'competitions',
    question: 'How are prizes paid?',
    answer: [
      `The brand checks the standings and awards each prize. It is paid through Affily like a commission: it appears in the winner’s earnings, the brand settles and pays it, and Affily’s fee is split between the brand and the winner as on any commission. A prize is awarded once and can’t be undone.`,
    ],
    related: ['how-do-i-get-paid', 'can-prizes-change'],
    keywords: 'prize award winner paid',
  },
  {
    slug: 'can-prizes-change',
    section: 'competitions',
    question: 'Can a brand change the prizes after I join?',
    answer: [
      'Only in creators’ favour. Once anyone has joined, prizes can be raised or added and the end moved later — never cut, removed, switched to another measure, or the end brought forward. How a counted action is counted can’t change either. Once the competition has ended, its dates are final.',
    ],
    related: ['how-do-competitions-work', 'can-i-change-my-terms'],
    keywords: 'change prize rules fair',
  },

  // ── Privacy and account ──────────────────────────────────────────────────
  {
    slug: 'what-is-public',
    section: 'account',
    question: 'What on my profile is public?',
    answer: [
      'A brand’s programs and their terms, and a creator’s published profile and rates. A profile is a draft — invisible, not in search — until its owner publishes it, and publishing needs a confirmed email.',
      'Followers read from a connected TikTok, Instagram, YouTube or Facebook Page are labelled verified, with the date they were read. Audience numbers a creator types in are shown as their own claim, labelled self-reported. Numbers Affily measured are labelled as measured.',
    ],
    related: ['transparency-score', 'visitor-privacy'],
    keywords: 'public profile draft publish self-reported verified followers',
  },
  {
    slug: 'sign-in-methods',
    section: 'account',
    question: 'Can I sign in with Google, Facebook or Instagram?',
    answer: [
      'Yes, as well as with your email and password, wherever the login page offers them:',
      {
        list: [
          '**Google** and **Facebook** share a confirmed email. If it already belongs to an Affily account, that account is opened and the new way in is added to it.',
          '**Instagram** works for business and creator accounts only, and shares no email. The first time, you give your email and confirm it from the link we send.',
        ],
      },
      'An email typed in after an Instagram sign-in never opens an existing account. If it is already on Affily, sign in the usual way and add Instagram from your Account page.',
      'The Account page, in your dashboard menu, lists every way in. You can add one, remove one, or set or change your password by an emailed link — but never remove the last way in.',
    ],
    related: ['what-is-public', 'api-keys-and-agents'],
    keywords: 'login sign in google facebook instagram password account social',
  },
  {
    slug: 'visitor-privacy',
    section: 'account',
    question: 'What does Affily keep about the people who click?',
    answer: [
      'To tell one visitor from another, Affily keeps a hashed form of their IP address and browser, made with a server-side secret, never the address itself. It doesn’t know who they are, and creators never see anything about a brand’s customers. [Privacy policy](/privacy).',
    ],
    related: ['what-do-i-see-about-my-referrals', 'what-is-public'],
    keywords: 'privacy data ip gdpr tracking cookies',
  },
  {
    slug: 'transparency-score',
    section: 'account',
    question: 'What is the transparency score?',
    answer: [
      'A score out of 100 for how much checkable information a creator’s profile carries — a measure of disclosure, not of quality, and nothing about it is for sale. Its rules are published in full, from the same code that computes it. [How transparency works](/transparency).',
    ],
    related: ['what-is-public'],
    keywords: 'score verified evidence',
  },
  {
    slug: 'api-keys-and-agents',
    section: 'account',
    question: 'Can an AI agent or a script use my account?',
    answer: [
      'Yes, with an API key you create under API keys: an agent can build your profile, upload your photos and set your rates — all as a draft you review. A key can never publish a profile, change a password, move money, award a prize or record a sale; those need you, signed in.',
    ],
    related: ['what-is-public'],
    keywords: 'api key agent automation claude',
  },
];

/** An article by slug, or undefined. Safe for untrusted input such as a URL. */
export function getHelpArticle(slug) {
  return HELP_ARTICLES.find((a) => a.slug === slug);
}

/** The answer as plain text, for search and for structured data. */
export function helpPlainText(article) {
  const strip = (s) => s.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\*\*([^*]+)\*\*/g, '$1');
  return article.answer
    .map((block) => (typeof block === 'string' ? strip(block) : block.list.map(strip).join(' ')))
    .join(' ');
}

/**
 * Articles matching a search, best first: every word must appear somewhere in
 * the question, keywords or answer; question hits rank above the rest.
 */
export function searchHelp(query) {
  const words = String(query ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return HELP_ARTICLES.map((a) => {
    const question = a.question.toLowerCase();
    const rest = `${a.keywords ?? ''} ${helpPlainText(a)}`.toLowerCase();
    if (!words.every((w) => question.includes(w) || rest.includes(w))) return null;
    return { article: a, score: words.filter((w) => question.includes(w)).length };
  })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.article);
}
